import { riverChainData, riverConfigFor } from '@1delta/data-sdk'
import { parseRawAmount } from '../../../utils/parsing'
import { isFailedCall } from '../../../utils/multicall'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMultiAccountTypeUserState } from '../utils'
import {
  LenderCrossPoolMeta,
  LenderToLenderCrossPoolMeta,
  UserData,
} from '../utils/types'
import { riverLenderKey } from '../../public-data/river/convertPublic'
import { RIVER_CALLS_PER_MARKET } from './userCallBuild'
import { rebuildRiverData, usesRiverLens } from './lens'

/** Per-market position detail attached to the debt row (raw strings). */
export interface RiverPositionInfo {
  /** TroveManager (market) index. */
  index: number
  /** Post-liquidation/redemption collateral surplus claimable via claimCollateral. */
  collateralSurplus: string
}

const big = (v: any): bigint => {
  try {
    if (typeof v === 'bigint') return v
    if (typeof v === 'number' || typeof v === 'string') return BigInt(v)
  } catch {}
  return 0n
}

/** Field access for decoded tuples that may arrive named or positional. */
const field = (res: any, name: string, idx: number): bigint =>
  big(res?.[name] ?? res?.[idx])

/**
 * Returns a [converter, expectedCallCount] tuple for River user data.
 *
 * Layout mirrors `buildRiverUserCall` (deterministic from config — no
 * discovery cache needed). Emits one `UserData` per market key
 * `RIVER_<chainId>_<TM_INDEX>`, each with a single sub-account `'0'` (troves are
 * address-keyed: at most ONE per user per market): collateral row
 * (entire coll incl. pending redistribution) + satUSD debt row (entire debt)
 * with `riverInfo` (collateral surplus). The single per-chain StabilityPool
 * deposit is attached as an `sp` sub-account on market index 0 (matching the
 * public-data convention).
 */
export const getRiverUserDataConverter = (
  lender: string,
  chainId: string,
  account: string,
  meta?: LenderToLenderCrossPoolMeta,
): [(data: any[]) => { [lender: string]: UserData } | undefined, number] => {
  const cfg = riverConfigFor(lender, chainId)
  const markets = riverChainData(lender, chainId)?.markets ?? []
  const expected =
    cfg && markets.length > 0 ? markets.length * RIVER_CALLS_PER_MARKET + 1 : 0
  const viaLens = expected > 0 && usesRiverLens(chainId)

  return [
    (raw: any[]) => {
      if (!cfg || expected === 0) return undefined
      if (!raw) return undefined
      if (viaLens && raw.length !== 1) return undefined

      // One blob in, the walk's own flat array out — the cursor arithmetic
      // below is identical on both paths, so they cannot drift in
      // interpretation. A declined rebuild is an incomplete read, never an
      // empty position.
      const data = viaLens ? rebuildRiverData(raw[0], markets.length) : raw
      if (!data || data.length < expected) return undefined

      const debtAddr = cfg.debtToken.toLowerCase()
      const debtDecimals = cfg.debtDecimals ?? 18
      const out: { [lender: string]: UserData } = {}
      const spDeposit = big(data[markets.length * RIVER_CALLS_PER_MARKET])

      markets.forEach((market, i) => {
        const base = i * RIVER_CALLS_PER_MARKET
        const entire = data[base]
        // A failed read is not an empty trove — skip the market rather than
        // reporting the user as having closed their position here.
        if (isFailedCall(entire) || isFailedCall(data[base + 1])) return
        const status = Number(big(data[base + 1]))
        const surplus = big(data[base + 2])

        const lenderKey = riverLenderKey(lender, chainId, market.index)
        const metaMap: LenderCrossPoolMeta | undefined = meta?.[lenderKey]
        if (!metaMap) return

        const collAddr = market.collToken.toLowerCase()
        const collUid = createMarketUid(chainId, lenderKey, collAddr)
        const loanUid = createMarketUid(chainId, lenderKey, debtAddr)
        const collMeta = metaMap[collUid]
        const loanMeta = metaMap[loanUid]
        const collDecimals = collMeta?.asset?.decimals ?? market.collDecimals
        const collDisplay = collMeta ? getDisplayPrice(collMeta) : 0
        const collOracle = collMeta ? getOraclePrice(collMeta) : 0
        const collHist = collMeta?.price?.priceUsd24h ?? collDisplay
        const loanDisplay = loanMeta ? getDisplayPrice(loanMeta) : 0
        const loanOracle = loanMeta ? getOraclePrice(loanMeta) : 0
        const loanHist = loanMeta?.price?.priceUsd24h ?? loanDisplay

        const lendingPositions: Record<string, any> = {}
        const modes: Record<string, number> = {}
        const hist: Record<
          string,
          { totalDeposits24h: number; totalDebt24h: number }
        > = {}

        // Active trove (status 1). getEntireDebtAndColl includes pending
        // redistribution rewards.
        const debt = field(entire, 'debt', 0)
        const coll = field(entire, 'coll', 1)
        if (status === 1 && (debt > 0n || coll > 0n)) {
          const riverInfo: RiverPositionInfo = {
            index: market.index,
            collateralSurplus: surplus.toString(),
          }
          const collStr = parseRawAmount(coll.toString(), collDecimals)
          const collNum = Number(collStr)
          const debtStr = parseRawAmount(debt.toString(), debtDecimals)
          const debtNum = Number(debtStr)

          lendingPositions['0'] = {
            [collUid]: {
              marketUid: collUid,
              underlying: collAddr,
              deposits: collStr,
              debt: '0',
              debtStable: '0',
              depositsUSD: collNum * collDisplay,
              debtUSD: 0,
              debtStableUSD: 0,
              depositsUSDOracle: collNum * collOracle,
              debtUSDOracle: 0,
              debtStableUSDOracle: 0,
              stableBorrowRate: '0',
              collateralEnabled: true,
              claimableRewards: 0,
            },
            [loanUid]: {
              marketUid: loanUid,
              underlying: debtAddr,
              deposits: '0',
              debt: debtStr,
              debtStable: '0',
              depositsUSD: 0,
              debtUSD: debtNum * loanDisplay,
              debtStableUSD: 0,
              depositsUSDOracle: 0,
              debtUSDOracle: debtNum * loanOracle,
              debtStableUSDOracle: 0,
              stableBorrowRate: '0',
              collateralEnabled: false,
              claimableRewards: 0,
              riverInfo,
            },
          }
          modes['0'] = 0
          hist['0'] = {
            totalDeposits24h: collNum * collHist,
            totalDebt24h: debtNum * loanHist,
          }
        }

        // Single per-chain SP deposit → `sp` sub-account on market 0 only.
        if (market.index === 0 && spDeposit > 0n) {
          const spStr = parseRawAmount(spDeposit.toString(), debtDecimals)
          const spNum = Number(spStr)
          lendingPositions['sp'] = {
            [loanUid]: {
              marketUid: loanUid,
              underlying: debtAddr,
              deposits: spStr,
              debt: '0',
              debtStable: '0',
              depositsUSD: spNum * loanDisplay,
              debtUSD: 0,
              debtStableUSD: 0,
              depositsUSDOracle: spNum * loanOracle,
              debtUSDOracle: 0,
              debtStableUSDOracle: 0,
              stableBorrowRate: '0',
              collateralEnabled: false,
              claimableRewards: 0,
            },
          }
          modes['sp'] = 0
          hist['sp'] = { totalDeposits24h: spNum * loanHist, totalDebt24h: 0 }
        }

        if (Object.keys(lendingPositions).length === 0) return

        const userData = createMultiAccountTypeUserState(
          { chainId, account, lendingPositions, modes } as any,
          metaMap,
          hist,
        )
        if (userData && userData.data.length > 0) out[lenderKey] = userData
      })

      return Object.keys(out).length > 0 ? out : undefined
    },
    // The CALL count the framework hands us: one blob on the lens path, the
    // whole per-manager walk otherwise. `expected` stays the SLOT count the
    // cursor arithmetic above is written against.
    viaLens ? 1 : expected,
  ]
}
